import { Component, ElementRef, inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { IContactFormDataKeys } from '../../../../components/forms/create-contact-form/create-contact-form.component';
import { countries } from '../../../../../utils/countriesList';
import { ContactsService } from '../../../../services/contacts/contacts.service';
import { ReactiveFormsModule, Validators, FormBuilder, FormArray, AbstractControl } from '@angular/forms';
import { InputFieldComponent } from '../../../../components/ui/inputField/inputField.component';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { GroupSelectionInputComponent } from '../../../../components/inputs/group-selection-input/group-selection-input.component';
import { IContact, IOrganization, ITag } from '../../../../types/contactModule';
import { AssignOrganizationsTableComponent } from '../../../../components/tables/assign-organizations-modal/assign-organizations-modal.component';
import { AssignTagsModalComponent } from '../../../../components/tables/assign-tags-modal/assign-tags-modal.component';
import { FeedbackCardComponent } from '../../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { formatCount } from '../../../../../utils/sms';

@Component({
  selector: 'app-multiple-contacts',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    NgxMatInputTelComponent,
    GroupSelectionInputComponent,
    AssignOrganizationsTableComponent,
    AssignTagsModalComponent,
    FeedbackCardComponent,
  ],
  templateUrl: './multiple-contacts.component.html',
  styleUrl: './multiple-contacts.component.css',
})
export class MultipleContactsComponent implements OnDestroy {
  router = inject(Router);
  private builder = inject(FormBuilder);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  contactService = inject(ContactsService);

  countries = countries;
  submitted: boolean = false;
  selectedContactIdx: number = 0;
  selectedTags: ITag[][] = [[]];
  selectedOrganizations: IOrganization[][] = [[]];
  /** Which rows show their optional fields. */
  expanded: boolean[] = [false];

  // feedback Messages
  successMsg: string = '';
  errorMsg: string = '';
  /** What the last save did, shown above the form until the next change. */
  resultMsg: string = '';
  /** False when the save added nobody (every number was already saved). */
  resultSaved: boolean = true;

  // timeout IDs for cleanup
  private successTimeoutId?: ReturnType<typeof setTimeout>;
  private errorTimeoutId?: ReturnType<typeof setTimeout>;

  // update feedback message
  updateFeedbackMsg(type: 'success' | 'error', message: string) {
    if (type === 'success') {
      if (this.successTimeoutId) {
        clearTimeout(this.successTimeoutId);
      }
      this.successMsg = message;
      this.successTimeoutId = setTimeout(() => {
        this.successMsg = '';
        this.successTimeoutId = undefined;
      }, 4000);
      this.errorMsg = '';
    } else {
      if (this.errorTimeoutId) {
        clearTimeout(this.errorTimeoutId);
      }
      this.errorMsg = message;
      this.errorTimeoutId = setTimeout(() => {
        this.errorMsg = '';
        this.errorTimeoutId = undefined;
      }, 4000);
      this.successMsg = '';
    }
  }

  // modal Controls
  showAddOrganizationModal: boolean = false;
  showAddTagModal: boolean = false;

  openAddOrganizationModal(idx: number) {
    this.selectedContactIdx = idx;
    this.showAddOrganizationModal = true;
  }
  closeAddOrganizationModal() {
    this.showAddOrganizationModal = false;
  }
  openAddTagModal(idx: number) {
    this.selectedContactIdx = idx;
    this.showAddTagModal = true;
  }
  closeAddTagModal() {
    this.showAddTagModal = false;
  }

  updateSelectedTags(selections: ITag[]) {
    this.selectedTags[this.selectedContactIdx] = [...(selections ?? [])];
  }

  updateSelectedOrganizations(selections: IOrganization[]) {
    this.selectedOrganizations[this.selectedContactIdx] = [...(selections ?? [])];
  }

  removeSelectedTag(tag: any, idx: number = this.selectedContactIdx) {
    const passedTag = tag as ITag;
    this.selectedTags[idx] = (this.selectedTags[idx] ?? []).filter((el) => el.id !== passedTag.id);
  }

  removeSelectedOrganization(org: any, idx: number = this.selectedContactIdx) {
    const passedorg = org as IOrganization;
    this.selectedOrganizations[idx] = (this.selectedOrganizations[idx] ?? []).filter(
      (el) => el.id !== passedorg.id
    );
  }

  private newRow() {
    return this.builder.group({
      firstName: ['', Validators.required],
      lastName: [''],
      emailAddress: ['', [Validators.email]],
      primaryContactNumber: ['', Validators.required],
      gender: [''],
      birthDate: [''],
      country: [''],
      city: [''],
      zipCode: [''],
    });
  }

  allContacts = this.builder.group({
    contacts: this.builder.array([this.newRow()]),
  });

  getFormInput(name: IContactFormDataKeys) {
    return this.addedContacts.get(name);
  }

  updateSelectedTagIndex(idx: number) {
    this.selectedContactIdx = idx;
  }

  /** Adds an empty row once every row so far is valid. */
  addContact() {
    this.submitted = true;
    if (this.allContacts.status == 'VALID') {
      this.addedContacts.push(this.newRow());
      this.selectedTags.push([]);
      this.selectedOrganizations.push([]);
      this.expanded.push(false);
      this.submitted = false;
      this.resultMsg = '';
      return;
    }
    this.reportFirstProblem();
  }

  /**
   * A row is invalid: say which one and what it needs, and put focus on the
   * first field with an error (it may be scrolled out of view).
   */
  private reportFirstProblem() {
    const rows = this.addedContacts.controls;
    const index = rows.findIndex((row) => row.invalid);
    if (index < 0) return;
    const row = rows[index];
    const needs: string[] = [];
    if (row.get('firstName')?.invalid) needs.push('a first name');
    const phone = row.get('primaryContactNumber');
    if (phone?.invalid) needs.push(phone.errors?.['required'] ? 'a phone number' : 'a valid phone number');
    if (row.get('emailAddress')?.invalid) needs.push('a valid email address');
    const list = needs.length > 1 ? `${needs.slice(0, -1).join(', ')} and ${needs[needs.length - 1]}` : needs[0];
    this.updateFeedbackMsg('error', list ? `Row ${index + 1} needs ${list}.` : `Row ${index + 1} needs fixing.`);
    // After change detection has marked the fields invalid.
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
  }

  /** "2 organizations · 1 tag" for a folded row. */
  moreHint(idx: number): string {
    const orgs = (this.selectedOrganizations[idx] ?? []).length;
    const tags = (this.selectedTags[idx] ?? []).length;
    return `${orgs} ${orgs === 1 ? 'organization' : 'organizations'} · ${tags} ${tags === 1 ? 'tag' : 'tags'}`;
  }

  removeContact(idx: number) {
    if (this.addedContacts.length <= 1) return;
    this.addedContacts.removeAt(idx);
    this.selectedTags.splice(idx, 1);
    this.selectedOrganizations.splice(idx, 1);
    this.expanded.splice(idx, 1);
  }

  /** "Ada Lovelace" once a name is typed, else ''. */
  rowTitle(row: AbstractControl): string {
    const first = (row.get('firstName')?.value ?? '').toString().trim();
    const last = (row.get('lastName')?.value ?? '').toString().trim();
    return `${first} ${last}`.trim().slice(0, 60);
  }

  toggleMore(idx: number) {
    this.expanded[idx] = !this.expanded[idx];
  }

  goToContactsPage() {
    this.router.navigate(['/organization/contacts']);
  }

  emailErrorMsg(email: any) {
    if (email?.errors?.['email']) {
      return 'Enter an email address like name@example.com';
    }
    return '';
  }

  phoneErrorMsg(phone: any) {
    if (!phone?.invalid) return '';
    return phone.errors?.['required'] ? 'Enter a phone number' : 'Enter a valid phone number';
  }

  get addedContacts() {
    return <FormArray>this.allContacts?.get('contacts');
  }

  get rowCount(): number {
    return this.addedContacts.length;
  }

  get submitLabel(): string {
    const count = this.rowCount;
    return count === 1 ? 'Save contact' : `Save ${count} contacts`;
  }

  private resetRows() {
    this.addedContacts.clear();
    this.addedContacts.push(this.newRow());
    this.selectedTags = [[]];
    this.selectedOrganizations = [[]];
    this.expanded = [false];
    this.submitted = false;
  }

  async handleSubmit() {
    this.submitted = true;
    if (!this.allContacts.valid) {
      this.reportFirstProblem();
      return;
    }
    const updatedWithTagsNOrganizations = this.allContacts.value.contacts?.map((el, idx) => {
      return {
        ...el,
        entityTags: this.selectedTags[idx] ?? [],
        companyEntities: this.selectedOrganizations[idx] ?? [],
      };
    });
    const sent = updatedWithTagsNOrganizations?.length ?? 0;
    this.contactService.createMultipleContacts(updatedWithTagsNOrganizations as IContact[]).subscribe({
      next: (res) => {
        // The API returns the contacts it saved; numbers already in the
        // organisation are skipped without an error.
        const saved = Array.isArray(res) ? res.length : sent;
        const skipped = Math.max(0, sent - saved);
        const savedText = saved === 1 ? 'Added 1 contact.' : `Added ${formatCount(saved)} contacts.`;
        this.resultMsg = skipped
          ? `${saved ? savedText + ' ' : 'No new contacts were added. '}${formatCount(skipped)} ${skipped === 1 ? 'was' : 'were'} skipped because the number is already in your contacts.`
          : savedText;
        this.resultSaved = saved > 0;
        // Clear the rows only once they are saved.
        this.resetRows();
      },
      error: () => {
        this.resultMsg = '';
        this.updateFeedbackMsg('error', 'The contacts were not saved. Your rows are still here; try again.');
      },
    });
  }

  ngOnDestroy(): void {
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
    }
    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
    }
  }
}
